import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useCallback, type ReactNode } from 'react';

import { Box } from '@/components/ui/box';
import { isRecommendedCaseLayout, useCaseLayout } from '@/services/chat/caseLayoutSwitch';
import { notifyCaseBoxLayout, registerCaseBox } from '@/services/chat/clinicalCaseHeader';

const KEEP_VIEW_PROPS = isWeb ? {} : { collapsable: false };

/**
 * Registers the whole opening bubble for the chat list to measure, under the recommended case
 * layout only: the header line, jump chip and peek wait until none of the bubble is on screen.
 * The current layout registers the case box instead, so this renders its children untouched.
 * Part of the temporary case layout switch.
 */
export function OpeningBubbleMeasure({
  conversationId,
  enabled,
  children,
}: {
  conversationId: string;
  /** True on the opening message only, the one bubble that carries the case. */
  enabled: boolean;
  children: ReactNode;
}) {
  const layout = useCaseLayout();

  const bubbleRef = useCallback(
    (node: Parameters<typeof registerCaseBox>[1]) => registerCaseBox(conversationId, node),
    [conversationId]
  );

  const handleLayout = useCallback(() => notifyCaseBoxLayout(conversationId), [conversationId]);

  if (!enabled || !isRecommendedCaseLayout(layout)) {
    return <>{children}</>;
  }

  return (
    <Box ref={bubbleRef} {...KEEP_VIEW_PROPS} onLayout={handleLayout}>
      {children}
    </Box>
  );
}
